/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-02-14 00:00:00
 *
 *******************************************************/
import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Page,
  Button,
  Layout,
  Card,
  Badge,
  Stack,
  EmptyState,
  ButtonGroup,
  Icon,
  Link,
  Tooltip,
  TextStyle,
  TextField,
  SkeletonDisplayText
} from '@shopify/polaris';
import { ExternalMinor, DuplicateMinor, ReadTimeMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { routes } from 'src/routes';
import history from 'src/routes/history';
import { getFullAppUrlV2 } from 'src/helpers';

import ThemeNotExist from 'src/components/containers/shopify-integration/theme-not-exist';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import ProgressBar from 'src/components/views/shopify-integration/progress-bar';
import SelectTemplateModal from 'src/components/containers/shopify-integration/select-template.modal';
import CustomizeTemplateModal from 'src/components/containers/shopify-integration/manage-template-instance/modal/customize-template.modal';
import WarningAddWidgetLiveThemeModal from 'src/components/views/shopify-integration/warning-add-widget-live-theme-modal';
import ErrorLimitSectionModal from 'src/components/views/shopify-integration/error-limit-section-modal';
import ErrorModal from 'src/components/views/shopify-integration/error-modal';
import AddWidgetThemeSuccessModal from 'src/components/views/shopify-integration/add-widget-theme-success-modal';
import EnableWidgetSuccessModal from 'src/components/views/shopify-integration/enable-widget-success-modal';
import EnableWidgetFailModal from 'src/components/views/shopify-integration/enable-widget-fail-modal';
import WidgetAlreadyAddedModal from 'src/components/views/shopify-integration/widget-already-added-modal';

import {
  navigateToShopifyCustomizeSSR,
  copyClipBoard
} from 'src/helpers/shopify-integration-helpers';
import { trackFSEvent } from 'src/integrations/fullStory';

import EmptyContent from 'src/assets/images/shopify-integration/EmptyContent.png';
import ThumbnailDefaultTemplate from 'src/assets/images/shopify-integration/ThumbnailDefaultTemplate.png';
import ThumbnailBasicTemplate from 'src/assets/images/shopify-integration/ThumbnailBasicTemplate.png';
import BoostSnippetDemo from 'src/assets/images/shopify-integration/BoostSnippetDemo.jpg';
import BoostLogo from 'src/assets/images/shopify-integration/BoostLogo.png';

import { URL } from 'src/constants/links';
import {
  WIDGET_FEATURES,
  LIMIT_CUSTOM_TEMPLATE_INSTANCE,
  ERROR_CODE,
  TEMPLATE_FEATURE
} from 'src/constants/shopify-integration';
import { SI_THEME_INTEGRATION_FEATURE_SUCCESS } from 'src/stores/flow-type/shopify-integration/theme-integration.type';

import * as shopifyIntegrationThemeAction from 'actions/shopify-integration/theme-actions';
import * as templateInstanceAction from 'actions/shopify-integration/template-instance-actions';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';

type Props = {};
type ComposedProps = Props & WithI18nProps;

type State = {};

class ShopifyIntegrationScreenNew extends Component<ComposedProps, State> {
  state = {
    integrationStep: 1,
    refreshingThemeList: false,
    openModal: false,
    integratedTemplateInstance: {},
    openCustomizeModal: false,
    isLoadingAddCollection: false,
    isLoadingAddSearch: false,
    isEnablingISW: false,
    openWarningAddWidgetLiveTheme: false,
    openErrorLimitSection: false,
    openErrorModal: false,
    openAddWidgetSuccess: false,
    openEnableWidgetSuccess: false,
    openEnableWidgetFail: false,
    openWidgetAlreadyAdded: false
    // Hardcode for testing
    // integratedTemplateInstance: {
    //   templateId: '40b33c6d-0c48-4755-9638-e4ab204806b0',
    //   templateVersion: '5.0.0',
    //   templateName: 'Rename Copy of Default 5',
    //   themeLibVersion: '1.0.0',
    //   themeNameLib: 'default',
    //   widgetIds: {
    //     collectionFilters: 'ftpl_075346a4-e17f-4a4d-8029-f6f496f5a63c',
    //     productList: 'prdgrid_4bbc61e0-e0d9-4b52-affc-274b4e1480e4',
    //     productItem: 'prditem_535dbaa8-1fd6-4970-967c-f1ea83209ed1',
    //     quickView: 'qv_ade4087a-cd62-4583-9710-15efd8b8a236',
    //     addToCart: 'atc_fe894e8d-e662-496d-998f-38c94b838bd1',
    //     placeholder: 'pl_ftpl_cce8d5a0-37f6-4cdd-be22-ff8be2bd355b'
    //   },
    //   tenantId: 'bc-dev-v2-phuong-5.myshopify.com',
    //   themeIds: [128298975313, 126973313105],
    //   refThemeId: '126973313105',
    //   clonedFromTemplateId: '64fc602c-0517-4287-b644-ac06bd7c8897',
    //   clonedFromDefault: true,
    //   createdAt: 1709695296,
    //   updatedAt: 1709715754,
    //   settingsCss: {
    //     id: 'gid://shopify/GenericFile/25521815257169',
    //     url: 'https://cdn.shopify.com/s/files/1/0090/6326/8433/files/boost-app-do-not-delete-this-file-bc-dev-v2-phuong-5__40b33c6d-0c48-4755-9638-e4ab204806b0-settings.css?v=1709715767',
    //     fileName:
    //       'boost-app-do-not-delete-this-file-bc-dev-v2-phuong-5__40b33c6d-0c48-4755-9638-e4ab204806b0-settings.css'
    //   }
    // }
  };

  async componentDidMount() {
    const {
      getBoostTemplateInstances,
      getTemplateInstances,
      getAllThemes,
      themes = [],
      checkThemeIntegrationStatus,
      match,
      boostTemplates,
      templateInstances
    } = this.props;

    const result = await checkThemeIntegrationStatus(match?.params?.themeId);
    const data = _.get(result, 'payload.data');
    if (!_.isEmpty(data)) {
      if (data.isDeleted) return;

      history.push(routes.shopifyIntegration.viewThemeWidget(match?.params?.themeId));
      return;
    }

    // Get Boost template instances
    if (_.isEmpty(boostTemplates)) {
      getBoostTemplateInstances();
    }

    // Get Template instances settings on theme
    if (_.isEmpty(templateInstances)) {
      getTemplateInstances();
    }

    if (!themes.length) {
      getAllThemes();
    }
  }

  componentWillReceiveProps(nextProps) {
    const { themes } = this.props;
    const { themes: nextThemes, updateFieldReducer, match } = nextProps;
    if (themes !== nextThemes) {
      const themeId = Number(match.params.themeId) || '';
      const themeInfo = nextThemes.find(item => item.id === themeId);
      if (themeInfo) {
        updateFieldReducer('selectedThemeId', themeInfo?.id);
      }
    }
  }

  componentWillUnmount() {
    this.props.cleanStateExitManageTemplatePage();
  }

  render() {
    const {
      integrationStep,
      openModal,
      integratedTemplateInstance = {},
      openCustomizeModal,
      openWarningAddWidgetLiveTheme,
      openErrorLimitSection,
      openErrorModal,
      openAddWidgetSuccess,
      openEnableWidgetSuccess,
      openEnableWidgetFail,
      openWidgetAlreadyAdded
    } = this.state;
    const {
      i18n,
      themes = [],
      selectedThemeId,
      boostTemplates = [],
      templateInstances = [],
      integratingTheme = false,
      duplicateTemplateLoading,
      checkingTheme,
      getAllThemesLoading,
      getBoostTemplatesLoading,
      getTemplateInstancesLoading,
      isThemeDeleted,
      emailSupport
    } = this.props;
    const isLoading =
      checkingTheme ||
      getAllThemesLoading ||
      getBoostTemplatesLoading ||
      getTemplateInstancesLoading;

    // Move selected template instance to top of list
    const sortedTemplateInstances = templateInstances;
    if (!_.isEmpty(integratedTemplateInstance)) {
      const [templateInstance] = _.remove(sortedTemplateInstances, {
        templateId: integratedTemplateInstance.templateId
      });

      if (templateInstance) sortedTemplateInstances.unshift(templateInstance);
    }

    // Get selected theme details
    const selectedThemeDetails = _.find(themes, { id: selectedThemeId });
    const templateId = _.get(integratedTemplateInstance, 'templateId', '');
    const isOS20 = _.get(selectedThemeDetails, 'isOS20', '');

    if (isThemeDeleted) return <ThemeNotExist />;

    return (
      <Page
        title={
          <div className="theme-setup-heading">
            <Stack spacing="tight">{this.renderPageTitle(isLoading)}</Stack>
          </div>
        }
        breadcrumbs={[
          {
            content: '',
            onAction: this.handleGoBack
          }
        ]}>
        <div className="integration-layout">
          <Layout>
            <Layout.Section>
              <Card>
                {/* Progress bar */}
                <Card.Section>
                  {isLoading ? (
                    <SkeletonDisplayText maxWidth="100%" />
                  ) : (
                    <ProgressBar step={integrationStep} />
                  )}
                </Card.Section>

                {isLoading ? (
                  <Card.Section>
                    <div className="loading-content">
                      <SkeletonDisplayText size="large" />
                    </div>
                  </Card.Section>
                ) : (
                  <Card.Section>
                    {_.isEmpty(integratedTemplateInstance) ? (
                      <div className="empty-content">
                        <EmptyState
                          fullWidth
                          heading={i18n.translate(
                            'AdminHTML.ShopifyIntegration.EmptyState.heading'
                          )}
                          action={{
                            content: i18n.translate(
                              'AdminHTML.ShopifyIntegration.Actions.selectTemplate'
                            ),
                            onAction: this.handleChangeModal
                          }}
                          image={EmptyContent}>
                          <p>{i18n.translate('AdminHTML.ShopifyIntegration.EmptyState.content')}</p>
                        </EmptyState>
                      </div>
                    ) : // Step: 1 => Confirm select instance
                    // Step: 2 => Guide setup widgets
                    integrationStep === 1 ? (
                      this.renderConfirmSelectInstance()
                    ) : integrationStep === 2 ? (
                      this.renderEnableBoostSnippet()
                    ) : (
                      this.renderGuideSetupWidgets()
                    )}
                  </Card.Section>
                )}

                {!_.isEmpty(integratedTemplateInstance) && (
                  <Card.Section>
                    {integrationStep === 1
                      ? this.renderConfirmSelectInstanceFooter()
                      : integrationStep === 2
                      ? this.renderEnableBoostSnippetFooter()
                      : this.renderGuideSetupWidgetsFooter()}
                  </Card.Section>
                )}
              </Card>
            </Layout.Section>
          </Layout>
        </div>

        <SelectTemplateModal
          open={openModal}
          disabling={integratingTheme}
          onClose={this.handleChangeModal}
          onConfirm={this.handleConfirmModal}
          integratedTemplateInstance={integratedTemplateInstance}
          boostTemplates={boostTemplates}
          templateInstances={sortedTemplateInstances}
          selectedTheme={selectedThemeDetails}
          match={this.props.match}
        />

        <CustomizeTemplateModal
          open={openCustomizeModal}
          loading={duplicateTemplateLoading || integratingTheme}
          onClose={this.handleCloseCustomizeTemplateModal}
          onConfirm={this.handleConfirmCustomizeModal}
          templateInstance={integratedTemplateInstance}
          isReachLimitCustomTemplate={templateInstances.length >= LIMIT_CUSTOM_TEMPLATE_INSTANCE}
        />

        <WarningAddWidgetLiveThemeModal
          open={openWarningAddWidgetLiveTheme}
          onClose={this.handleCloseWarningAddWidgetLive}
          onConfirm={this.handleAddWidget}
          feature={this.pathId}
        />

        <ErrorLimitSectionModal
          open={openErrorLimitSection}
          selectedThemeId={selectedThemeId}
          onClose={this.handleCloseErrorLimitSection}
          feature={this.pathId}
        />

        <ErrorModal
          open={openErrorModal}
          selectedThemeId={selectedThemeId}
          templateId={templateId}
          onClose={this.handleCloseErrorModal}
          emailSupport={emailSupport}
          feature={this.pathId}
        />

        <AddWidgetThemeSuccessModal
          open={openAddWidgetSuccess}
          selectedThemeId={selectedThemeId}
          templateId={templateId}
          onClose={this.handleCloseSuccessModal}
          emailSupport={emailSupport}
          feature={this.pathId}
          isOS20={isOS20}
        />

        <EnableWidgetSuccessModal
          open={openEnableWidgetSuccess}
          selectedThemeId={selectedThemeId}
          onClose={this.handleCloseEnableWidgetSuccessModal}
          emailSupport={emailSupport}
        />

        <EnableWidgetFailModal
          open={openEnableWidgetFail}
          templateId={templateId}
          selectedThemeId={selectedThemeId}
          onClose={this.handleCloseEnableWidgetFailModal}
          emailSupport={emailSupport}
        />

        <WidgetAlreadyAddedModal
          open={openWidgetAlreadyAdded}
          selectedThemeId={selectedThemeId}
          onClose={this.handleCloseWidgetAlreadyAdded}
          feature={this.pathId}
          isOS20={isOS20}
        />
      </Page>
    );
  }

  renderPageTitle = isLoading => {
    const { i18n, selectedThemeId, themes = {} } = this.props;

    // Get theme details
    const themeDetails = _.find(themes, { id: selectedThemeId });

    const themeName = _.get(themeDetails, 'name', '');
    const themeRole = _.get(themeDetails, 'role', '');
    const isOS20 = _.get(themeDetails, 'isOS20', '');
    const OSText = isOS20 ? '(OS 2.0)' : '(OS 1.0)';

    const badgeThemeRole =
      themeRole === 'main' ? (
        <Badge status="info">
          {i18n.translate('AdminHTML.ShopifyIntegration.ThemeStatus.liveTheme')}
        </Badge>
      ) : (
        <Badge>{i18n.translate('AdminHTML.ShopifyIntegration.ThemeStatus.unpublishedTheme')}</Badge>
      );

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', minWidth: isLoading ? '250px' : 'unset' }}>
        {isLoading ? (
          <SkeletonDisplayText maxWidth="100%" />
        ) : (
          <>
            {themeName} {OSText} {badgeThemeRole}
          </>
        )}
      </div>
    );
  };

  renderConfirmSelectInstance = () => {
    const { i18n } = this.props;
    const { integratedTemplateInstance = {} } = this.state;

    const thumbnails = {
      default: ThumbnailDefaultTemplate,
      dawn: ThumbnailBasicTemplate
    };

    const { templateName, templateId, themeNameLib, createdAt, updatedAt } =
      integratedTemplateInstance;
    let lastSavedAt = updatedAt || createdAt;
    lastSavedAt = moment(lastSavedAt * 1000).format('MMM DD, YYYY [at] h:mm A');

    return (
      <div className="confirm-select-instance">
        {/* Title */}
        <div className="title">
          {i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplate.NewTemplate')}
        </div>

        <div className="instance">
          {/* Image thumbnail represents specific theme lib */}
          <img className="thumbnail" src={thumbnails[themeNameLib]} width={90} height={72} alt="" />

          {/* Template info */}
          <div className="details">
            <div className="info-title">
              {_.upperFirst(templateName)}{' '}
              <Badge status="success">
                {i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplate.badgeCurrentTemplate')}
              </Badge>
            </div>
            <div className="description">
              Template ID: {templateId}{' '}
              <span className="copy">
                <Button
                  icon={DuplicateMinor}
                  plain
                  onClick={() => copyClipBoard(i18n, templateId)}
                />
              </span>
            </div>
            <div className="description">Last saved: {lastSavedAt}</div>
          </div>

          {/* Actions */}
          <div className="action">
            <ButtonGroup>
              {/* Change template */}
              <Button onClick={this.handleChangeModal}>
                {i18n.translate('AdminHTML.ShopifyIntegration.Actions.change')}
              </Button>

              {/* Customization */}
              <Button onClick={this.handleCheckNavigateToVisualEditor}>
                {i18n.translate('AdminHTML.ShopifyIntegration.Actions.customize')}
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </div>
    );
  };

  renderConfirmSelectInstanceFooter = () => {
    const { i18n } = this.props;

    return (
      <div className="footer">
        <Button primary onClick={this.handleNextToBoostSnippet} disabled={false} loading={false}>
          {i18n.translate('AdminHTML.ShopifyIntegration.Actions.btnNext')}
        </Button>
      </div>
    );
  };

  renderEnableBoostSnippet = () => {
    const { i18n, selectedThemeId } = this.props;
    const { integratedTemplateInstance = {} } = this.state;

    const { templateId } = integratedTemplateInstance;

    return (
      <div className="boost-snippet">
        {/* Instruction */}
        <div className="left-col">
          <div className="heading">
            {i18n.translate('AdminHTML.ShopifyIntegration.EnableBoostSnippet.heading', {
              t1: (
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.ShopifyIntegration.EnableBoostSnippet.heading_t1')}
                </TextStyle>
              )
            })}
          </div>

          {/* Enable snippet */}
          <div className="enable-snippet">
            {/* Image thumbnail represents specific theme lib */}
            <img className="thumbnail" src={BoostLogo} width={40} height={40} alt="" />

            {/* Template info */}
            <div className="details">
              <div className="info-title">
                {i18n.translate('AdminHTML.ShopifyIntegration.UpdateInstance.appEmbedName')}
              </div>
              <div className="description">
                {i18n.translate('AdminHTML.ShopifyIntegration.UpdateInstance.appEmbedBoostName')}
              </div>
            </div>

            {/* View settings */}
            <div className="btn-enable">
              <Button
                size="slim"
                onClick={() => {
                  navigateToShopifyCustomizeSSR(selectedThemeId, true);
                }}>
                {i18n.translate('AdminHTML.ShopifyIntegration.Actions.enable')}
              </Button>
            </div>
          </div>

          {/* Instruction */}
          <div className="manual-instruction">
            <p>
              {i18n.translate('AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction', {
                t1: (
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_t1'
                    )}
                  </TextStyle>
                )
              })}
            </p>
            <ul>
              <li>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step1',
                  {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step1_t1'
                        )}
                      </TextStyle>
                    ),
                    t2: (
                      <Link
                        external
                        onClick={() => {
                          navigateToShopifyCustomizeSSR(selectedThemeId, false);
                        }}>
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step1_t2'
                        )}
                      </Link>
                    )
                  }
                )}
              </li>
              <li>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step2',
                  {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step2_t1'
                        )}
                      </TextStyle>
                    )
                  }
                )}
              </li>
              <li>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step3',
                  {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step3_t1'
                        )}
                      </TextStyle>
                    )
                  }
                )}
              </li>
            </ul>
            <div>
              <TextField
                value={templateId}
                disabled={true}
                suffix={
                  <span className="copy">
                    <Button
                      icon={DuplicateMinor}
                      plain
                      onClick={() => copyClipBoard(i18n, templateId)}
                    />
                  </span>
                }
              />
            </div>
          </div>
        </div>

        {/* Illustration image */}
        <div className="right-col">
          <img
            className="thumbnail clickable"
            src={BoostSnippetDemo}
            width={414}
            height={250}
            alt=""
            onClick={() => window.open(BoostSnippetDemo, '_blank', 'noopener,noreferrer')}
          />
        </div>
      </div>
    );
  };

  renderEnableBoostSnippetFooter = () => {
    const { i18n } = this.props;

    return (
      <div className="footer">
        <ButtonGroup>
          <Button onClick={this.handleBackToConfirmInstance}>
            {i18n.translate('AdminHTML.ShopifyIntegration.Actions.back')}
          </Button>
          <Button primary onClick={this.handleNextToSetupWidget}>
            {i18n.translate('AdminHTML.ShopifyIntegration.Actions.btnNext')}
          </Button>
        </ButtonGroup>
      </div>
    );
  };

  renderGuideSetupWidgets = () => {
    const { i18n } = this.props;
    const { isLoadingAddCollection, isLoadingAddSearch, isEnablingISW } = this.state;
    const isLiveTheme = this.checkLiveTheme();

    const MAPPING_BUTTON_TEXT = {
      [TEMPLATE_FEATURE.COLLECTION]: i18n.translate('AdminHTML.ShopifyIntegration.Actions.add'),
      [TEMPLATE_FEATURE.SEARCH]: i18n.translate('AdminHTML.ShopifyIntegration.Actions.add'),
      [TEMPLATE_FEATURE.ISW]: i18n.translate('AdminHTML.ShopifyIntegration.Actions.enable'),
      [TEMPLATE_FEATURE.RECOMMENDATION]: i18n.translate(
        'AdminHTML.ShopifyIntegration.Actions.manage'
      )
    };

    const tooltipAction = {
      [TEMPLATE_FEATURE.COLLECTION]: i18n.translate(
        'AdminHTML.ShopifyIntegration.Actions.addWidget'
      ),
      [TEMPLATE_FEATURE.SEARCH]: i18n.translate('AdminHTML.ShopifyIntegration.Actions.addWidget'),
      [TEMPLATE_FEATURE.ISW]: i18n.translate('AdminHTML.ShopifyIntegration.Actions.enableWidget'),
      [TEMPLATE_FEATURE.RECOMMENDATION]: i18n.translate(
        'AdminHTML.ShopifyIntegration.Actions.manageWidget'
      )
    };

    return (
      <div className="setup-widgets">
        <div className="title">
          {i18n.translate('AdminHTML.ShopifyIntegration.SetupWidgets.SetupIntroduction', {
            t1: (
              <Link external url={URL['BoostTemplateWidgets']}>
                {i18n.translate('AdminHTML.ShopifyIntegration.SetupWidgets.SetupIntroduction_1')}
                <Icon source={ExternalMinor} color="interactive" />
              </Link>
            )
          })}
        </div>
        <div className="features">
          {_.map(WIDGET_FEATURES, feature => {
            return (
              <div key={feature.name} className="feature-item">
                <div>{feature.name}</div>
                <div className="feature-action">
                  <Tooltip
                    content={i18n.translate(
                      'AdminHTML.ShopifyIntegration.SetupWidgets.ViewGuideTooltip'
                    )}
                    preferredPosition="above">
                    <Button onClick={() => this.handleGoToFeaturePage(feature.pathId)}>
                      <Icon source={ReadTimeMinor} color="interactive" />
                    </Button>
                  </Tooltip>
                  {((isLiveTheme && feature.pathId === TEMPLATE_FEATURE.RECOMMENDATION) ||
                    feature.pathId !== TEMPLATE_FEATURE.RECOMMENDATION) && (
                    <Tooltip
                      content={tooltipAction[feature.pathId]}
                      preferredPosition="above"
                      activatorWrapper="div">
                      <Button
                        loading={
                          this.pathId === feature.pathId &&
                          (isLoadingAddCollection || isLoadingAddSearch || isEnablingISW)
                        }
                        onClick={() => this.handleAction(feature.pathId, feature.featureId)}>
                        {MAPPING_BUTTON_TEXT[feature.pathId]}
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  renderGuideSetupWidgetsFooter = () => {
    const { i18n } = this.props;

    return (
      <div className="footer">
        <ButtonGroup>
          <Button onClick={this.handleBackToBoostSnippet}>
            {i18n.translate('AdminHTML.ShopifyIntegration.Actions.back')}
          </Button>
          <Button primary onClick={this.handleFinishSetup}>
            {i18n.translate('AdminHTML.ShopifyIntegration.Actions.finish')}
          </Button>
        </ButtonGroup>
      </div>
    );
  };

  handleGoBack = () => {
    // const { handleChangeScreen } = this.props;
    // handleChangeScreen('list');
    history.push(routes.shopifyIntegration.view());
  };

  handleChangeModal = () => {
    const { openModal } = this.state;
    this.setState({ openModal: !openModal });
  };

  handleConfirmModal = async (templateId, themeNameLib, isBoostTemplate, callback) => {
    const { i18n, themeIntegrationSetup, selectedThemeId, addCreatedTemplateInstance, themes } =
      this.props;

    // Call setup and then add new created template instance to state
    await themeIntegrationSetup(selectedThemeId, templateId, themeNameLib, isBoostTemplate).then(
      async response => {
        const { type = '' } = response;

        if (type === 'SI_THEME_INTEGRATION_SETUP_SUCCESS') {
          const createdTemplateInstance = this.props.createdTemplateInstance;

          if (isBoostTemplate && !_.isEmpty(createdTemplateInstance)) {
            addCreatedTemplateInstance(createdTemplateInstance, isBoostTemplate);
          }

          // Save state
          this.setState({ integratedTemplateInstance: createdTemplateInstance });

          showSABToast(
            i18n.translate('AdminHTML.ShopifyIntegration.SetupWidgets.toastTemplateSaveSuccess'),
            false
          );

          // Track Full Story event
          const themeDetails = _.find(themes, { id: selectedThemeId });
          trackFSEvent('Select A Template', {
            feature: 'SSR Theme Setup',
            themeName: themeDetails?.name,
            templateName: createdTemplateInstance.templateName,
            templateId: createdTemplateInstance.templateId
          });

          // Close modal
          this.setState({ openModal: false });
          callback && callback();
        }
      }
    );
  };

  handleNextToBoostSnippet = () => {
    this.setState({ integrationStep: 2 });
  };

  handleNextToSetupWidget = () => {
    this.setState({ integrationStep: 3 });
  };

  handleBackToConfirmInstance = () => {
    this.setState({ integrationStep: 1 });
  };

  handleBackToBoostSnippet = () => {
    this.setState({ integrationStep: 2 });
  };

  handleFinishSetup = () => {
    const { saveIntegratedTemplateInstance, match } = this.props;
    const { integratedTemplateInstance } = this.state;

    // Save integratedTemplateInstance to parent state component
    saveIntegratedTemplateInstance(integratedTemplateInstance);

    // Go to Update integration screen
    history.push(routes.shopifyIntegration.viewThemeWidget(match.params?.themeId));
    // handleChangeScreen('update-template-integration');
  };

  handleGoToFeaturePage = pathId => {
    const { selectedThemeId, getAllThemes } = this.props;
    const { integratedTemplateInstance = {} } = this.state;

    getAllThemes();

    // Get themeId, templateId
    const templateId = _.get(integratedTemplateInstance, 'templateId', '');

    history.push({
      pathname: routes.shopifyIntegration.guide(selectedThemeId, templateId, pathId)
    });
  };

  handleCheckNavigateToVisualEditor = () => {
    const { selectedThemeId, themes = [] } = this.props;
    const { integratedTemplateInstance = {} } = this.state;

    const templateId = _.get(integratedTemplateInstance, 'templateId', '');

    const themeDetails = _.find(themes, { id: selectedThemeId });
    const isLiveTheme = themeDetails.role === 'main';
    const linkedWithLiveTheme = this.checkLinkedWithLiveTheme(integratedTemplateInstance);

    // Show warning modal: Unpublished theme and Template linked with live theme
    if (!isLiveTheme && linkedWithLiveTheme) {
      this.setState({
        openCustomizeModal: true
      });
    } else {
      const visualEditorURL = getFullAppUrlV2(
        routes.shopifyIntegrationSettings.visualEditor(templateId)
      );
      window.open(visualEditorURL, '_blank');
    }
  };

  handleCloseCustomizeTemplateModal = () => {
    const { openCustomizeModal } = this.state;
    this.setState({ openCustomizeModal: !openCustomizeModal });
  };

  checkLinkedWithLiveTheme = templateInstance => {
    const { themes = [] } = this.props;
    const themeIds = _.get(templateInstance, 'themeIds', []);

    let linkedWithLiveTheme = false;
    for (const themeId of themeIds) {
      if (linkedWithLiveTheme) break;

      const themeDetails = _.find(themes, { id: themeId });
      if (!_.isEmpty(themeDetails) && themeDetails.role === 'main') {
        linkedWithLiveTheme = true;
      }
    }

    return linkedWithLiveTheme;
  };

  handleConfirmCustomizeModal = async () => {
    const { integratedTemplateInstance = {} } = this.state;
    const { templateId, themeNameLib } = integratedTemplateInstance;
    const { i18n, duplicateTemplateInstance } = this.props;

    if (!templateId) {
      return;
    }

    await duplicateTemplateInstance(templateId).then(async response => {
      const { type = '' } = response;
      if (type === 'SI_DUPLICATE_TEMPLATE_SUCCESS') {
        showSABToast(
          i18n.translate(
            'AdminHTML.ShopifyIntegration.ManageTemplateInstance.duplicateTemplateSuccess'
          ),
          false
        );

        this.handleConfirmModal(
          _.get(response, 'payload.data.templateId'),
          themeNameLib,
          false,
          this.handleCloseCustomizeTemplateModal
        );
      }
    });
  };

  handleAction = async (pathId, featureId) => {
    this.pathId = pathId;
    this.featureId = featureId;
    const { themes, selectedThemeId, themeIntegrationIntegrateFeature } = this.props;
    const themeInfo = _.find(themes, { id: selectedThemeId });
    const isLiveTheme = themeInfo?.role === 'main';

    switch (pathId) {
      case TEMPLATE_FEATURE.COLLECTION:
      case TEMPLATE_FEATURE.SEARCH:
        if (isLiveTheme) {
          this.setState({
            openWarningAddWidgetLiveTheme: true
          });
        } else {
          this.processAddWidget();
        }
        break;
      case TEMPLATE_FEATURE.ISW:
        this.setState({
          isEnablingISW: true
        });

        const result = await themeIntegrationIntegrateFeature(selectedThemeId, featureId);
        const state = {
          isEnablingISW: false
        };

        if (result?.type === SI_THEME_INTEGRATION_FEATURE_SUCCESS) {
          state.openEnableWidgetSuccess = true;
        } else {
          state.openEnableWidgetFail = true;
        }

        this.setState(state);
        break;
      case TEMPLATE_FEATURE.RECOMMENDATION:
        const recommendationURL = getFullAppUrlV2(routes.recommendationWidgets.list(1)); // Draft tab
        window.open(recommendationURL, '_blank');
        break;
      default:
        break;
    }
  };
  handleCloseWarningAddWidgetLive = () => {
    this.setState({
      openWarningAddWidgetLiveTheme: false
    });
  };

  processAddWidget = async () => {
    const { selectedThemeId, themeIntegrationIntegrateFeature } = this.props;

    const isLoadingField =
      this.pathId === TEMPLATE_FEATURE.COLLECTION ? 'isLoadingAddCollection' : 'isLoadingAddSearch';

    this.setState({
      [isLoadingField]: true
    });
    const result = await themeIntegrationIntegrateFeature(selectedThemeId, this.featureId);
    const state = {
      [isLoadingField]: false
    };

    if (result?.type === SI_THEME_INTEGRATION_FEATURE_SUCCESS) {
      state.openAddWidgetSuccess = true;
    } else {
      const errorCode = _.get(result, 'payload.data.errorCode');
      switch (errorCode) {
        case ERROR_CODE.REACH_LIMIT: {
          state.openErrorLimitSection = true;
          break;
        }
        case ERROR_CODE.ALREADY_ADDED: {
          state.openWidgetAlreadyAdded = true;
          break;
        }
        default:
          state.openErrorModal = true;
      }
    }

    this.setState(state);
  };

  handleAddWidget = async () => {
    this.processAddWidget();
    this.setState({
      openWarningAddWidgetLiveTheme: false
    });
  };

  handleCloseErrorLimitSection = () => {
    this.setState({
      openErrorLimitSection: false
    });
  };

  handleCloseErrorModal = () => {
    this.setState({
      openErrorModal: false
    });
  };

  handleCloseSuccessModal = () => {
    this.setState({
      openAddWidgetSuccess: false
    });
  };

  handleCloseEnableWidgetSuccessModal = () => {
    this.setState({
      openEnableWidgetSuccess: false
    });
  };

  handleCloseEnableWidgetFailModal = () => {
    this.setState({
      openEnableWidgetFail: false
    });
  };

  handleCloseWidgetAlreadyAdded = () => {
    this.setState({
      openWidgetAlreadyAdded: false
    });
  };

  checkLiveTheme = () => {
    const { themes, selectedThemeId } = this.props;
    const themeDetails = _.find(themes, { id: selectedThemeId });
    const themeRole = _.get(themeDetails, 'role', '');
    return themeRole === 'main';
  };
}

const mapStateToProps = (state, props) => {
  const { shopifyIntegrationTheme = {}, templateInstance = {}, themeIntegration = {} } = state;

  const themes = _.get(shopifyIntegrationTheme, 'allThemes', []);
  const boostTemplates = _.get(templateInstance, 'boostTemplates', []);
  const templateInstances = _.get(templateInstance, 'templateInstances', []);

  // Theme integration setup
  const integratingTheme = _.get(themeIntegration, 'integratingTheme', false);
  const createdTemplateInstance = _.get(themeIntegration, 'createdTemplateInstance', {});

  const duplicateTemplateLoading = _.get(templateInstance, 'duplicateTemplateLoading', false);
  const duplicatedTemplateInstance = _.get(templateInstance, 'duplicatedTemplateInstance', {});
  const integratedTemplateInstance = _.get(themeIntegration, 'templateInstance', {});

  return {
    emailSupport: _.get(state.oauth, 'emailSupport'),

    // All shopify themes
    themes,
    selectedThemeId: _.get(themeIntegration, 'selectedThemeId', ''),

    // Boost template instance, store template instance
    boostTemplates,
    templateInstances,

    // Theme integration setup
    integratingTheme,
    createdTemplateInstance,
    integratedTemplateInstance,

    duplicateTemplateLoading,
    duplicatedTemplateInstance,

    checkingTheme: _.get(themeIntegration, 'checkingTheme', false),
    getAllThemesLoading: _.get(shopifyIntegrationTheme, 'getAllThemesLoading', false),
    getBoostTemplatesLoading: _.get(templateInstance, 'getBoostTemplatesLoading', false),
    getTemplateInstancesLoading: _.get(templateInstance, 'getTemplateInstancesLoading', false),
    isThemeDeleted: _.get(integratedTemplateInstance, 'isDeleted', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getAllThemes: () => dispatch(shopifyIntegrationThemeAction.getAllThemes(ownProps.i18n)),
  getBoostTemplateInstances: () =>
    dispatch(templateInstanceAction.getBoostTemplateInstances(ownProps.i18n)),
  getTemplateInstances: () => dispatch(templateInstanceAction.getTemplateInstances(ownProps.i18n)),
  themeIntegrationSetup: (themeId, templateId, themeNameLib, isBoostTemplate) =>
    dispatch(
      themeIntegrationAction.themeIntegrationSetup(
        ownProps.i18n,
        themeId,
        templateId,
        themeNameLib,
        isBoostTemplate
      )
    ),
  themeIntegrationIntegrateFeature: (themeId, feature) =>
    dispatch(
      themeIntegrationAction.themeIntegrationIntegrateFeature(themeId, feature, ownProps.i18n)
    ),
  addCreatedTemplateInstance: (templateInstance, isBoostTemplate) =>
    dispatch(templateInstanceAction.addCreatedTemplateInstance(templateInstance, isBoostTemplate)),
  checkThemeIntegrationStatus: themeId =>
    dispatch(themeIntegrationAction.checkThemeIntegrationStatus(ownProps.i18n, themeId)),
  updateFieldReducer: (fieldName, data) =>
    dispatch(themeIntegrationAction.handleUpdateField(fieldName, data)),
  duplicateTemplateInstance: templateId =>
    dispatch(templateInstanceAction.duplicateTemplateInstance(ownProps.i18n, templateId)),
  cleanStateExitManageTemplatePage: () =>
    dispatch(templateInstanceAction.cleanStateExitManageTemplatePage())
});

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(ShopifyIntegrationScreenNew)
);
